import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '@/lib/api';
import { bindForegroundMessages, getFirebasePushToken } from '@/lib/firebaseMessaging';

const POLL_INTERVAL_MS = 30000;
const SEEN_STORAGE_KEY = 'bf_seen_notifications';
const DEVICE_UID_STORAGE_KEY = 'bf_device_uid';

function getDeviceUid() {
  if (typeof window === 'undefined') return '';
  const existing = localStorage.getItem(DEVICE_UID_STORAGE_KEY);
  if (existing) return existing;
  const generated = (window.crypto && window.crypto.randomUUID)
    ? window.crypto.randomUUID()
    : `bf-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  localStorage.setItem(DEVICE_UID_STORAGE_KEY, generated);
  return generated;
}

function getSeenIds() {
  if (typeof window === 'undefined') return new Set();
  try {
    const parsed = JSON.parse(localStorage.getItem(SEEN_STORAGE_KEY) || '[]');
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

function persistSeenIds(ids) {
  if (typeof window === 'undefined') return;
  const payload = Array.from(ids).slice(0, 300);
  localStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify(payload));
}

function getBrowserPermissionState() {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

function showDesktopNotification(title, body) {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted') return;
  try {
    const notification = new Notification(title || 'Notification', { body: body || '' });
    setTimeout(() => notification.close(), 8000);
  } catch {
    // ignore
  }
}

export default function useSystemNotifications({ enabled = true } = {}) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [permissionState, setPermissionState] = useState(getBrowserPermissionState);
  const [pushStatus, setPushStatus] = useState({ supported: false, configured: false, tokenRegistered: false });
  const seenIdsRef = useRef(getSeenIds());

  const markFetchedAsSeen = useCallback((items) => {
    for (const item of items) {
      seenIdsRef.current.add(item.id);
    }
    persistSeenIds(seenIdsRef.current);
  }, []);

  const refresh = useCallback(async ({ silent = false } = {}) => {
    if (!enabled) return [];
    if (!silent) setLoading(true);
    try {
      const response = await api.get('/v1/notifications', { params: { limit: 60 } });
      const items = Array.isArray(response.data) ? response.data : [];
      setNotifications(items);

      for (const item of items) {
        if (!seenIdsRef.current.has(item.id) && permissionState === 'granted') {
          showDesktopNotification(item.title, item.message);
        }
      }
      markFetchedAsSeen(items);
      return items;
    } catch {
      if (!silent) setNotifications([]);
      return [];
    } finally {
      if (!silent) setLoading(false);
    }
  }, [enabled, markFetchedAsSeen, permissionState]);

  const registerDevice = useCallback(async () => {
    if (!enabled || typeof window === 'undefined') return;
    const device_uid = getDeviceUid();
    const language = localStorage.getItem('bf_lang') || navigator.language || 'en';
    const browser = navigator.userAgentData?.brands?.map((entry) => entry.brand).join(', ') || navigator.userAgent || 'browser';
    const basePayload = {
      device_uid,
      permission_state: permissionState,
      browser,
      user_agent: navigator.userAgent || '',
      language,
    };

    let token = '';
    let supported = false;
    let configured = false;
    if (permissionState === 'granted') {
      const firebaseToken = await getFirebasePushToken();
      token = firebaseToken.token || '';
      supported = Boolean(firebaseToken.supported);
      configured = Boolean(firebaseToken.configured);
    }

    try {
      const response = await api.post('/v1/notifications/register-device', {
        ...basePayload,
        token,
      });
      setPushStatus({
        supported,
        configured,
        tokenRegistered: Boolean(response.data?.token_registered),
      });
    } catch {
      setPushStatus({ supported, configured, tokenRegistered: false });
    }
  }, [enabled, permissionState]);

  const requestPermission = useCallback(async () => {
    if (typeof window === 'undefined' || typeof Notification === 'undefined') {
      setPermissionState('unsupported');
      return 'unsupported';
    }
    let nextState = Notification.permission;
    if (nextState === 'default') {
      nextState = await Notification.requestPermission();
    }
    setPermissionState(nextState);
    return nextState;
  }, []);

  const markDone = useCallback(async (notificationId) => {
    await api.post(`/v1/notifications/${notificationId}/mark-done`, {});
    await refresh({ silent: true });
  }, [refresh]);

  useEffect(() => {
    if (!enabled) return undefined;
    refresh({ silent: false });
    const timer = setInterval(() => refresh({ silent: true }), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [enabled, refresh]);

  useEffect(() => {
    if (!enabled) return undefined;
    bindForegroundMessages((payload) => {
      const title = payload?.notification?.title || payload?.data?.title || 'Notification';
      const message = payload?.notification?.body || payload?.data?.message || '';
      showDesktopNotification(title, message);
      refresh({ silent: true });
    });
    return undefined;
  }, [enabled, refresh]);

  useEffect(() => {
    if (!enabled) return;
    if (permissionState === 'unsupported') return;
    registerDevice();
  }, [enabled, permissionState, registerDevice]);

  const unreadCount = useMemo(() => notifications.length, [notifications]);

  return {
    notifications,
    unreadCount,
    loading,
    permissionState,
    pushStatus,
    refresh,
    requestPermission,
    markDone,
  };
}
